
    import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
    import { Getter, Mutation } from 'vuex-class';
    import * as jschardet from 'jschardet';

    interface ParsedRow {
        name: string;
        locationGroup: number | null;
        longitude: number;
        latitude: number;
        props: { [propertyId: number]: string };
    }

    @Component
    export default class BulkUploadLocationsDialog extends Vue {
        @Prop({ default: null }) public bulkUploadLocations: any;
        @Prop({ default: null }) public model: any;

        @Getter('getFileFormatCulture') getFileFormatCulture!: any;
        @Mutation('SET_FILEFORMATCULTURE') setFileFormatCulture!: any;

        private modalShow = false;
        private file: File | null = null;
        private scenarioName = '';
        private rows: ParsedRow[] = [];
        private propertyColumns: any[] = [];
        private parseError = '';
        private cultureWarning = '';

        private get fileFormatCulture(): any {
            return this.getFileFormatCulture;
        }

        private set fileFormatCulture(value: any) {
            this.setFileFormatCulture(value);
        }

        private get languages() {
            return [
                { key: 'nl', text: this.$t('message.dutchFormat') },
                { key: 'en', text: this.$t('message.englishFormat') },
            ];
        }

        private mounted() {
            this.$on('escKey' + this.$options.name, () => {
                if (this.modalShow) this.close();
            });
            document.addEventListener("keydown", (e) => {
                if (e.key === "Escape") {
                    this.$emit('escKey' + this.$options.name);
                }
            });
        }

        @Watch('bulkUploadLocations')
        private onPayloadChanged(value: any) {
            if (value) {
                this.file = null;
                this.scenarioName = '';
                this.rows = [];
                this.propertyColumns = [];
                this.parseError = '';
                this.cultureWarning = '';
                this.modalShow = true;
            } else {
                this.modalShow = false;
            }
        }

        @Watch('fileFormatCulture')
        private async onCultureChanged() {
            if (this.file) await this.parseFile(this.file);
        }

        private get modelLocationProperties(): any[] {
            return (this.bulkUploadLocations && this.bulkUploadLocations.modelLocationProperties) || [];
        }

        private get locationGroupItems(): any[] {
            if (!this.model || !this.model.locationGroups) return [];
            return this.model.locationGroups.filter((g: any) => g.selectable !== false);
        }

        private get headers() {
            const base = [
                { text: this.$t('message.name'), value: 'name' },
                { text: this.$t('message.locationGroup'), value: 'locationGroup' },
                { text: this.$t('message.longitude'), value: 'longitude' },
                { text: this.$t('message.latitude'), value: 'latitude' },
            ];
            const extra = this.propertyColumns.map(p => ({
                text: p.title || p.name,
                value: 'prop_' + p.id,
                sortable: false,
            }));
            return [...base, ...extra];
        }

        private get invalidRowCount(): number {
            return this.rows.filter(r => !r.name || !r.locationGroup).length;
        }

        private get canSave(): boolean {
            return this.rows.length > 0 && this.invalidRowCount === 0 && !!this.scenarioName && !this.parseError;
        }

        private scenarioNameRule(v: string) {
            return !!v || String(this.$t('message.required'));
        }

        private propSlotName(prop: any): string {
            return 'item.prop_' + prop.id;
        }

        // Delimiter follows the user-selected fileFormatCulture (Vuex getFileFormatCulture,
        // bound through Main.vue). Matches the export side: ; for nl/de/fr, , for en.
        private get csvDelimiter(): string {
            const c = this.fileFormatCulture || '';
            return (c.startsWith('nl') || c.startsWith('de') || c.startsWith('fr')) ? ';' : ',';
        }

        // Heuristic: look at the header line and return 'nl' (semicolon-dominant) or 'en'
        // (comma-dominant) when one delimiter clearly outnumbers the other. Returns null
        // when the signal is too weak to be sure (e.g. single-column files).
        private detectFileCulture(text: string): 'nl' | 'en' | null {
            const firstLine = text.split(/\r?\n/, 1)[0] || '';
            const semi = (firstLine.match(/;/g) || []).length;
            const comma = (firstLine.match(/,/g) || []).length;
            if (semi >= 3 && semi > comma * 3) return 'nl';
            if (comma >= 3 && comma > semi * 3) return 'en';
            return null;
        }

        private parseNumber(raw: string): number {
            if (raw == null) return NaN;
            const trimmed = raw.trim();
            if (this.fileFormatCulture && (this.fileFormatCulture.startsWith('nl') || this.fileFormatCulture.startsWith('de') || this.fileFormatCulture.startsWith('fr'))) {
                return parseFloat(trimmed.replace(/\./g, '').replace(',', '.'));
            }
            return parseFloat(trimmed.replace(/,/g, ''));
        }

        // Decode the uploaded file with charset detection that mirrors the backend's
        // IRISCsvReader (Ude). BOMs are honoured first (cheap + definitive); otherwise
        // jschardet — a port of Mozilla's universal charset detector, same family as Ude —
        // identifies the encoding. Falls back to Windows-1252 on low confidence or an
        // unknown label (covers legacy Western-European CSV exports from Excel).
        private async readFileText(file: File): Promise<string> {
            const buffer = await file.arrayBuffer();
            const bytes = new Uint8Array(buffer);
            if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
                return new TextDecoder('utf-8').decode(bytes.subarray(3));
            }
            if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xFE) {
                return new TextDecoder('utf-16le').decode(bytes.subarray(2));
            }
            if (bytes.length >= 2 && bytes[0] === 0xFE && bytes[1] === 0xFF) {
                return new TextDecoder('utf-16be').decode(bytes.subarray(2));
            }
            // jschardet expects a binary string; build in chunks to avoid blowing the stack.
            let binary = '';
            const chunk = 8192;
            for (let i = 0; i < bytes.length; i += chunk) {
                binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
            }
            const detected = jschardet.detect(binary);
            const encoding = detected && detected.encoding && detected.confidence > 0.5
                ? detected.encoding
                : 'windows-1252';
            try {
                return new TextDecoder(encoding).decode(bytes);
            } catch {
                return new TextDecoder('windows-1252').decode(bytes);
            }
        }

        // RFC 4180-style parser: honours double-quoted fields (delimiter and newlines inside
        // quotes are content), escaped quotes (""), and \r\n or \n row terminators.
        private parseCsv(text: string, delimiter: string): string[][] {
            const rows: string[][] = [];
            let row: string[] = [];
            let cell = '';
            let inQuotes = false;
            for (let i = 0; i < text.length; i++) {
                const ch = text[i];
                if (inQuotes) {
                    if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++; }
                    else if (ch === '"') { inQuotes = false; }
                    else { cell += ch; }
                } else {
                    if (ch === '"') { inQuotes = true; }
                    else if (ch === delimiter) { row.push(cell); cell = ''; }
                    else if (ch === '\r' || ch === '\n') {
                        if (ch === '\r' && text[i + 1] === '\n') i++;
                        row.push(cell); cell = '';
                        if (row.some(c => c.length > 0)) rows.push(row);
                        row = [];
                    } else { cell += ch; }
                }
            }
            if (cell.length > 0 || row.length > 0) {
                row.push(cell);
                if (row.some(c => c.length > 0)) rows.push(row);
            }
            return rows;
        }

        private async onFileSelected(file: File | null) {
            if (file) {
                // Default the scenario name to the CSV filename (without extension); user can edit it.
                this.scenarioName = file.name.replace(/\.csv$/i, '');
            }
            await this.parseFile(file);
        }

        private async parseFile(file: File | null) {
            this.rows = [];
            this.propertyColumns = [];
            this.parseError = '';
            this.cultureWarning = '';
            if (!file) return;
            const text = await this.readFileText(file);
            const detected = this.detectFileCulture(text);
            const selected: 'nl' | 'en' = (this.fileFormatCulture || '').startsWith('nl') ? 'nl' : 'en';
            if (detected && detected !== selected) {
                this.cultureWarning = String(this.$t('message.uploadLocationsCsvCultureMismatch', {
                    detected: this.$t(detected === 'nl' ? 'message.dutchFormat' : 'message.englishFormat'),
                    selected: this.$t(selected === 'nl' ? 'message.dutchFormat' : 'message.englishFormat'),
                }));
            }
            const parsed = this.parseCsv(text, this.csvDelimiter);
            if (parsed.length < 2) {
                this.parseError = String(this.$t('message.uploadLocationsCsvEmpty'));
                return;
            }
            const headerCells = parsed[0].map(h => h.trim());
            const headerLower = headerCells.map(h => h.toLowerCase());
            const nameIdx = headerLower.indexOf('name');
            const lonIdx = headerLower.findIndex(h => h === 'longitude' || h === 'lon' || h === 'lng');
            const latIdx = headerLower.findIndex(h => h === 'latitude' || h === 'lat');
            const groupIdx = headerLower.findIndex(h => h === 'locationgroup' || h === 'location group' || h === 'group');
            if (lonIdx === -1 || latIdx === -1) {
                this.parseError = String(this.$t('message.uploadLocationsCsvMissingColumns'));
                return;
            }

            // Detect columns that match a model location-property by name (case-insensitive).
            // Standard columns (name/longitude/latitude/locationgroup) are excluded so they are not treated as properties.
            const reservedIdx = new Set([nameIdx, lonIdx, latIdx, groupIdx].filter(i => i >= 0));
            const detectedProps: { columnIndex: number; property: any }[] = [];
            for (let i = 0; i < headerCells.length; i++) {
                if (reservedIdx.has(i)) continue;
                const cellLower = headerLower[i];
                const matched = this.modelLocationProperties.find((lp: any) =>
                    (lp.name && lp.name.toLowerCase() === cellLower) ||
                    (lp.title && lp.title.toLowerCase() === cellLower));
                if (matched) detectedProps.push({ columnIndex: i, property: matched });
            }
            this.propertyColumns = detectedProps.map(d => d.property);

            const groups = this.locationGroupItems;
            const parsedRows: ParsedRow[] = [];
            for (let i = 1; i < parsed.length; i++) {
                const cols = parsed[i].map(c => c.trim());
                const name = nameIdx >= 0 ? cols[nameIdx] : '';
                const lon = this.parseNumber(cols[lonIdx]);
                const lat = this.parseNumber(cols[latIdx]);
                if (isNaN(lon) || isNaN(lat)) continue;
                let groupId: number | null = null;
                if (groupIdx >= 0) {
                    const raw = cols[groupIdx];
                    if (raw) {
                        const match = groups.find((g: any) =>
                            String(g.id) === raw ||
                            (g.name && g.name.toLowerCase() === raw.toLowerCase()) ||
                            (g.code && g.code.toLowerCase() === raw.toLowerCase()));
                        if (match) groupId = match.id;
                    }
                }
                if (groupId == null && groups.length === 1) groupId = groups[0].id;

                const props: { [id: number]: string } = {};
                for (const dp of detectedProps) {
                    const raw = cols[dp.columnIndex] != null ? cols[dp.columnIndex] : '';
                    props[dp.property.id] = raw;
                }
                parsedRows.push({ name, locationGroup: groupId, longitude: lon, latitude: lat, props });
            }

            if (parsedRows.length === 0) {
                this.parseError = String(this.$t('message.uploadLocationsCsvNoRows'));
                return;
            }
            this.rows = parsedRows;
        }

        private async close() {
            const res = await this.$confirm(String(this.$t('message.confirmClose')), { buttonTrueText: String(this.$t('message.yes')), buttonFalseText: String(this.$t('message.no')) });
            if (res) this.$emit('cancel');
        }

        private submit() {
            const locations = this.rows.map(row => {
                const locationProperties = this.propertyColumns
                    .map(p => {
                        const raw = row.props[p.id];
                        if (raw == null || raw === '') return null;
                        const entry: any = { id: p.id, name: p.name, dataType: p.dataType };
                        if (p.dataType === 0) {
                            entry.stringValue = String(raw);
                        } else {
                            const num = this.parseNumber(String(raw));
                            if (isNaN(num)) return null;
                            entry.numberValue = num;
                        }
                        return entry;
                    })
                    .filter((e: any) => e != null);
                return {
                    name: row.name,
                    locationGroup: row.locationGroup,
                    longitude: row.longitude,
                    latitude: row.latitude,
                    locationProperties,
                };
            });
            this.$emit('save', {
                scenarioId: this.bulkUploadLocations.scenarioId,
                scenarioName: this.scenarioName,
                locations,
            });
        }
    }
